/* ===========================================================================
   ROVE NEXT — Add-Ons page behaviour. Rewritten 28 August 2026.

   Everything here is scoped to the add-ons section, so nothing can reach the
   homepage or a PDP.

   WHAT IT DOES
   1. VARIANT SELECTION. Size pills and colourway swatches rewrite the hidden
      input[name="id"], the price, the struck compare price, the Add to Cart
      state and — where the variant carries its own photograph — the card
      image. Sharon, 28 Aug: "prices are not changing" and "I click on the
      button and it's not changing [the colour]".
   2. HOVER PREVIEW, added 21 Sep 2026, Stevie: "physically click on the swatch
      icons for images to change to that fabric... can you also make it so if
      they also hover, it does the same thing." Hovering a swatch or size pill
      now previews its image and price the same way a click does, WITHOUT
      actually selecting it — the hidden id input, the pill's "on" state and
      the Add to Cart button are untouched by a hover, and moving the mouse
      away reverts the card to whatever is genuinely selected. A hover that
      silently changed what gets added to cart would be a real bug, not a
      convenience, so selection itself still only ever happens on click.
   3. ADD TO CART from the card, posting to /cart/add.js.
   4. The compatibility FILTER, which is dormant: the simplified page renders
      no [data-ao-filter] controls, so this simply finds nothing to bind. It is
      kept whole so filter tabs can be switched back on with markup alone once
      the assortment justifies them.

   WHY DELEGATION. The previous version bound a click handler to every pill at
   boot. Any card that failed its guard — a missing button, a late render, a
   theme-editor reload — silently lost its handlers, and a dead pill looks
   exactly like a working one. Listening once on the section root removes that
   whole class of failure: a pill works because it is in the DOM, not because
   it was present at the moment the script ran.

   The drawer is NOT opened here. rn-pdp-cart.js wraps window.fetch and opens
   it on any /cart/add response, so the section emits that script too and the
   two compose. Doing it here as well would open it twice.
   =========================================================================== */
(function () {
  /* C$495 / C$2,495 — matches the store money format, which is
     "C${{amount_no_decimals}}" (Settings > Store details). Liquid renders the
     first paint through | money; this only has to agree with it. */
  function money(cents) {
    var n = Math.round(cents / 100);
    return 'C$' + n.toLocaleString('en-CA');
  }

  function num(el, attr) {
    var v = parseInt(el.getAttribute(attr), 10);
    return isNaN(v) ? 0 : v;
  }

  /* Shared by both the real selection (click) and the preview (hover): writes
     the price, the struck compare price and the card photograph from a
     pill's own data attributes. Never touches the hidden id input, a pill's
     "on" state, or the Add to Cart button — those are what make a click a
     real selection rather than a preview, so they stay in selectVariant only. */
  function applyVisual(card, pill) {
    var now  = card.querySelector('[data-ao-now]');
    var was  = card.querySelector('[data-ao-was]');
    var shot = card.querySelector('.ao-shot');

    var price = num(pill, 'data-price');
    var cmp   = num(pill, 'data-cmp');
    if (now) now.textContent = price > 0 ? money(price) : '';
    /* The struck figure appears ONLY where the compare price is genuinely
       higher — never a fake anchor. */
    if (was) {
      if (cmp > price && price > 0) { was.textContent = money(cmp); was.hidden = false; }
      else { was.textContent = ''; was.hidden = true; }
    }

    /* srcset wins over src, so it has to be cleared before the swap or the
       browser keeps painting the old colourway. */
    var img = pill.getAttribute('data-img');
    if (shot && img) {
      shot.removeAttribute('srcset');
      shot.setAttribute('src', img);
    }
  }

  function selectVariant(card, pill) {
    var idInput = card.querySelector('input[name="id"]');
    var btn     = card.querySelector('[data-ao-add]');
    if (!idInput) return;

    card.querySelectorAll('[data-ao-pill]').forEach(function (p) {
      var on = p === pill;
      p.classList.toggle('on', on);
      p.setAttribute('aria-pressed', on ? 'true' : 'false');
    });

    idInput.value = pill.getAttribute('data-vid');
    applyVisual(card, pill);

    if (btn) {
      var avail = pill.getAttribute('data-avail') !== 'false';
      btn.disabled = !avail;
      btn.textContent = avail ? btn.getAttribute('data-label') : 'SOLD OUT';
    }
  }

  /* Hover preview: same visual update as a selection, none of the state. */
  function previewVariant(card, pill) {
    applyVisual(card, pill);
  }

  /* Mouse leaves a pill (and isn't just moving onto another pill in the same
     group): put the card's image/price back to whatever is actually
     selected, not whatever was last hovered. */
  function revertToSelected(card) {
    var selected = card.querySelector('[data-ao-pill].on');
    if (selected) applyVisual(card, selected);
  }

  function addToCart(card, btn) {
    var idInput = card.querySelector('input[name="id"]');
    if (!idInput || btn.disabled) return;
    var label = btn.getAttribute('data-label');
    btn.disabled = true;
    btn.textContent = 'ADDING';
    window.fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(idInput.value), quantity: 1 }] })
    })
      .then(function (r) { if (!r.ok) throw new Error('add failed'); return r.json(); })
      .then(function () {
        btn.textContent = 'ADDED';
        setTimeout(function () { btn.textContent = label; btn.disabled = false; }, 1700);
      })
      .catch(function (err) {
        console.warn('[rn-addons]', err);
        btn.textContent = 'TRY AGAIN';
        setTimeout(function () { btn.textContent = label; btn.disabled = false; }, 1900);
      });
  }

  function applyFilter(root, want) {
    root.setAttribute('data-piece', want);
    root.querySelectorAll('[data-ao-filter]').forEach(function (b) {
      var on = b.getAttribute('data-ao-filter') === want;
      b.classList.toggle('on', on);
      b.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
    root.querySelectorAll('[data-ao-card]').forEach(function (c) {
      var piece = c.getAttribute('data-piece');
      /* "all" is an accessory: it fits everything, so it never filters out. */
      var show = want === 'all' || piece === 'all' || piece === want;
      c.classList.toggle('ao-off', !show);
    });
  }

  function initSection(root) {
    if (root.__rnAddons) return;
    root.__rnAddons = true;

    root.addEventListener('click', function (e) {
      var pill = e.target.closest('[data-ao-pill]');
      if (pill && root.contains(pill)) {
        var card = pill.closest('[data-ao-card]');
        if (card) { selectVariant(card, pill); }
        return;
      }
      var filter = e.target.closest('[data-ao-filter]');
      if (filter && root.contains(filter)) {
        applyFilter(root, filter.getAttribute('data-ao-filter'));
      }
    });

    /* mouseover/mouseout rather than mouseenter/mouseleave: delegation on the
       root needs the bubbling versions, and closest() plus the
       relatedTarget check below does the same "don't fire between children
       of the same pill" filtering mouseenter/mouseleave would otherwise give
       for free. */
    root.addEventListener('mouseover', function (e) {
      var pill = e.target.closest('[data-ao-pill]');
      if (!pill || !root.contains(pill)) return;
      var card = pill.closest('[data-ao-card]');
      if (card) previewVariant(card, pill);
    });

    root.addEventListener('mouseout', function (e) {
      var pill = e.target.closest('[data-ao-pill]');
      if (!pill || !root.contains(pill)) return;
      if (e.relatedTarget && pill.contains(e.relatedTarget)) return;
      var card = pill.closest('[data-ao-card]');
      if (card) revertToSelected(card);
    });

    /* Submitting is its own event — a card's form must never navigate. */
    root.addEventListener('submit', function (e) {
      var form = e.target;
      if (!form.matches || !form.matches('form')) return;
      var card = form.closest('[data-ao-card]');
      var btn  = card && card.querySelector('[data-ao-add]');
      if (!card || !btn) return;
      e.preventDefault();
      addToCart(card, btn);
    });
  }

  function boot() {
    document.querySelectorAll('[data-ao-root]').forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  /* Theme editor re-renders the section without a page load. */
  document.addEventListener('shopify:section:load', boot);
})();
